// Need to use the React-specific entry point to import createApi
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

import { prepareDefaultHeaders } from '.'
import type {
  CreativeAd,
  CreativeAdDetail,
  CreativeTag,
  CreativeTagDetailData,
  GameCreativeDetailsData,
  GenreCreativeAggregate,
  MotivationComparisonDetailData,
  MotivationDetailData,
  MotivationDiagnosisDetailData,
  ObjectId,
  Page,
  ShowcaseData,
  StoryPatternsData,
  TrackedGame,
  UAInsightsDashboardData,
  UAInsightsMetaResponse,
} from '../features/ua-insights/types'

const REDUCER_PATH = 'uaInsightsApi'

/** Month / network scope shared by every scoped endpoint. */
type MonthNetworkParams = {
  networkId?: ObjectId
  month?: string
}

/** Network / month / genre scope. */
type ScopeParams = MonthNetworkParams & {
  genreId?: ObjectId
}

type PageParams = {
  /** Page size, 1–1000; the API defaults to 100. */
  limit?: number
  offset?: number
}

export type CreativesSort = 'seq' | 'installShare'

export type CreativesParams = ScopeParams &
  PageParams & {
    gameId?: ObjectId
    tagId?: ObjectId
    dominantMotivationId?: ObjectId
    /** Creatives whose motivation profile value for this motivation is at least 3.0 (scale 0–5). */
    carriesMotivationId?: ObjectId
    /** A story pattern or a sub-pattern. */
    patternId?: ObjectId
    sort?: CreativesSort
  }

export type TagsSort = 'creativesCount' | 'deltaPp' | 'name'

export type TagsParams = ScopeParams &
  PageParams & {
    dimensionId?: ObjectId
    /** Also list tag values with no creatives in scope. */
    includeEmpty?: boolean
    sort?: TagsSort
    /** Case-insensitive substring of the tag name. */
    q?: string
    minCreatives?: number
  }

type CreativesResponse = {
  creatives: CreativeAd[]
  page: Page
}

type GamesResponse = {
  games: TrackedGame[]
}

type TagsResponse = {
  tags: CreativeTag[]
  page: Page
}

type ShowcaseParams = ScopeParams & {
  lensMotivationId?: ObjectId
  gameId?: ObjectId
}

type CreativeAdDetailParams = MonthNetworkParams & { creativeId: ObjectId }

type GameScopedParams = MonthNetworkParams & { gameId: ObjectId }

/** At most one of `targetGameId` / `targetGenreId`; neither benchmarks against the game's own genre. */
type GameStoryPatternsParams = GameScopedParams & {
  targetGameId?: ObjectId
  targetGenreId?: ObjectId
}

type GenreCreativeAggregateParams = MonthNetworkParams & {
  genreId: ObjectId
  excludeGameId?: ObjectId
}

type MotivationParams = MonthNetworkParams & { motivationId: ObjectId }

/** Exactly one of `targetGameId` / `targetGenreId`. */
type MotivationComparisonParams = MotivationParams & {
  sourceGameId: ObjectId
  targetGameId?: ObjectId
  targetGenreId?: ObjectId
}

type MotivationDiagnosisParams = MotivationParams & { gameId: ObjectId }

type TagDetailParams = ScopeParams & {
  tagId: ObjectId
  gameId?: ObjectId
}

export const uaInsightsApi = createApi({
  reducerPath: REDUCER_PATH,
  baseQuery: fetchBaseQuery({
    baseUrl: window.GR_API_URLS.API_URL_UA_INSIGHTS,
    prepareHeaders: prepareDefaultHeaders,
  }),
  endpoints: (builder) => ({
    getMeta: builder.query<UAInsightsMetaResponse, void>({
      query: () => ({
        url: `/meta`,
      }),
    }),
    getDashboard: builder.query<UAInsightsDashboardData, ScopeParams>({
      query: (params) => ({
        url: `/dashboard`,
        params,
      }),
    }),
    getShowcase: builder.query<ShowcaseData, ShowcaseParams>({
      query: (params) => ({
        url: `/showcase`,
        params,
      }),
    }),
    getCreatives: builder.query<CreativesResponse, CreativesParams>({
      query: (params) => ({
        url: `/creatives`,
        params,
      }),
    }),
    getCreativeAdDetail: builder.query<CreativeAdDetail, CreativeAdDetailParams>({
      query: ({ creativeId, ...params }) => ({
        url: `/creatives/${creativeId}`,
        params,
      }),
    }),
    getGames: builder.query<GamesResponse, MonthNetworkParams>({
      query: (params) => ({
        url: `/games`,
        params,
      }),
    }),
    getTags: builder.query<TagsResponse, TagsParams>({
      query: (params) => ({
        url: `/tags`,
        params,
      }),
    }),
    getTagDetail: builder.query<CreativeTagDetailData, TagDetailParams>({
      query: ({ tagId, ...params }) => ({
        url: `/tags/${tagId}`,
        params,
      }),
    }),
    getGameCreativeDetails: builder.query<GameCreativeDetailsData, GameScopedParams>({
      query: ({ gameId, ...params }) => ({
        url: `/games/${gameId}/creative-details`,
        params,
      }),
    }),
    getGameStoryPatterns: builder.query<StoryPatternsData, GameStoryPatternsParams>({
      query: ({ gameId, ...params }) => ({
        url: `/games/${gameId}/story-patterns`,
        params,
      }),
    }),
    getGenreCreativeAggregate: builder.query<GenreCreativeAggregate, GenreCreativeAggregateParams>({
      query: ({ genreId, ...params }) => ({
        url: `/genres/${genreId}/creative-aggregate`,
        params,
      }),
    }),
    getMotivationDetail: builder.query<MotivationDetailData, MotivationParams>({
      query: ({ motivationId, ...params }) => ({
        url: `/motivations/${motivationId}`,
        params,
      }),
    }),
    getMotivationDiagnosis: builder.query<MotivationDiagnosisDetailData, MotivationDiagnosisParams>({
      query: ({ motivationId, ...params }) => ({
        url: `/motivations/${motivationId}/diagnosis`,
        params,
      }),
    }),
    getMotivationComparison: builder.query<MotivationComparisonDetailData, MotivationComparisonParams>({
      query: ({ motivationId, ...params }) => ({
        url: `/motivations/${motivationId}/comparison`,
        params,
      }),
    }),
  }),
})

// Export hooks to use in function components
export const {
  useGetMetaQuery,
  useGetDashboardQuery,
  useGetShowcaseQuery,
  useGetCreativesQuery,
  useGetCreativeAdDetailQuery,
  useGetGamesQuery,
  useGetTagsQuery,
  useGetTagDetailQuery,
  useGetGameCreativeDetailsQuery,
  useGetGameStoryPatternsQuery,
  useGetGenreCreativeAggregateQuery,
  useGetMotivationDetailQuery,
  useGetMotivationDiagnosisQuery,
  useGetMotivationComparisonQuery,
} = uaInsightsApi
